
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  HStack,
  Badge,
  Button,
  IconButton,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { RefreshCw } from 'lucide-react'
import { userActivityService, type ActivityStatistics as ActivityStatisticsData } from '@/lib/services/userActivityService'
import { useThemeColors } from '@/hooks/useThemeColors'

interface ActivityStatisticsProps {
  userId: number
}

export function ActivityStatistics({ userId }: ActivityStatisticsProps) {
  const colors = useThemeColors()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statistics, setStatistics] = useState<ActivityStatisticsData | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadStatistics()
  }, [userId])

  const loadStatistics = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)
      const response = await userActivityService.getActivityStatistics(userId, {
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
      })
      setStatistics(response.data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activity statistics')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = () => {
    loadStatistics(true)
  }

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={2}>Loading statistics...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    )
  }

  if (!statistics) {
    return (
      <Box textAlign="center" py={4}>
        <Text color={colors.text}>No statistics available</Text>
      </Box>
    )
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <VStack spacing={4} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          {statistics?.date_range && (
            <Text fontSize="xs" color={colors.text}>
              Date Range: {formatDate(statistics.date_range.start_date)} - {formatDate(statistics.date_range.end_date)}
            </Text>
          )}
          {statistics?.last_updated && (
            <Text fontSize="xs" color={colors.text}>
              Last Updated: {new Date(statistics.last_updated).toLocaleString()}
            </Text>
          )}
        </Box>
        <IconButton
          aria-label="Refresh statistics"
          icon={<RefreshCw size={16} />}
          size="sm"
          onClick={handleRefresh}
          isLoading={refreshing}
          variant="outline"
        />
      </HStack>
      <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Activities</StatLabel>
                <StatNumber>{statistics.total_activities}</StatNumber>
                <StatHelpText>Last 30 days</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Successful Logins</StatLabel>
                <StatNumber color="green.500">{statistics.login_count}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Failed Logins</StatLabel>
                <StatNumber color="red.500">{statistics.failed_login_count}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Logouts</StatLabel>
                <StatNumber>{statistics.logout_count}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Profile Updates</StatLabel>
                <StatNumber>{statistics.profile_updates}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>API Requests</StatLabel>
                <StatNumber>{statistics.api_requests}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4}>
        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="sm">Activities by Module</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={2} align="stretch">
                {Object.entries(statistics.by_module)
                  .sort(([, a], [, b]) => b - a)
                  .map(([module, count]) => (
                    <HStack key={module} justify="space-between">
                      <Text fontSize="sm">{module}</Text>
                      <Badge colorScheme="blue">{count}</Badge>
                    </HStack>
                  ))}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="sm">Activities by Action</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={2} align="stretch">
                {Object.entries(statistics.by_action)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([action, count]) => (
                    <HStack key={action} justify="space-between">
                      <Text fontSize="sm">{action}</Text>
                      <Badge colorScheme="green">{count}</Badge>
                    </HStack>
                  ))}
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </VStack>
  )
}

